import { useRef, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import toast from "react-hot-toast";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Textarea } from "@/components/ui/Textarea";
import { FormField } from "@/components/ui/FormField";
import {
  venueOnboardingSchema,
  type VenueOnboardingFormValues,
  COUNTRIES,
  BOOKING_SYSTEM_SUGGESTIONS,
} from "./schema";
import { submitVenueApplication } from "./submitApplication";

const BOOKING_SYSTEM_DATALIST_ID = "booking-system-suggestions";

/**
 * Minimum seconds a real human would spend reading and filling out the form
 * before hitting submit. Anything faster is almost certainly a headless
 * browser / script; we silently accept the submission so bots think they
 * succeeded but skip the actual write.
 */
const MIN_HUMAN_FILL_SECONDS = 3;

/**
 * Hidden honeypot field. If a bot autofills every input (very common
 * behaviour), they'll set this too; a real user never sees or touches it.
 */
const HONEYPOT_FIELD_NAME = "website_url";

export function VenueOnboardingForm() {
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const mountedAt = useRef<number>(Date.now());
  const honeypotRef = useRef<HTMLInputElement | null>(null);

  const form = useForm<VenueOnboardingFormValues>({
    resolver: zodResolver(venueOnboardingSchema),
    defaultValues: {
      venue_name: "",
      country: "GB",
      address_line1: "",
      address_line2: "",
      city: "",
      region: "",
      postcode: "",
      contact_name: "",
      contact_email: "",
      contact_phone: "",
      uses_booking_system: "yes",
      booking_system: "",
      notes: "",
      accept_terms: false as unknown as true,
    },
    mode: "onTouched",
  });

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = form;

  const usesBookingSystem = watch("uses_booking_system");

  const onSubmit = handleSubmit(async (values) => {
    // --- Spam protection (honeypot + timing) -----------------------------
    // Bots that auto-fill every text input will also fill this hidden input.
    // If it's touched at all, silently pretend success instead of writing.
    if (honeypotRef.current && honeypotRef.current.value.trim() !== "") {
      setSubmittedId("blocked"); // shows success screen w/o side effect
      return;
    }
    // Reject anything submitted impossibly fast - real users take at least a
    // few seconds to read and fill a form this long. A sub-3s submission is
    // effectively always a script.
    const elapsedSeconds = (Date.now() - mountedAt.current) / 1000;
    if (elapsedSeconds < MIN_HUMAN_FILL_SECONDS) {
      setSubmittedId("blocked");
      return;
    }
    // ----------------------------------------------------------------------

    try {
      const { applicationId } = await submitVenueApplication(values);
      setSubmittedId(applicationId);
      toast.success("Application submitted. We'll be in touch.");
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Something went wrong.";
      toast.error(message);
    }
  });

  if (submittedId) {
    return <SubmittedConfirmation id={submittedId} />;
  }

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-10" noValidate>
      {/* Honeypot field - hidden from humans via inline styles + aria,
          visible-and-autofillable to most bots. Real users never touch it;
          any non-empty value causes the submit handler to no-op. */}
      <input
        ref={honeypotRef}
        type="text"
        name={HONEYPOT_FIELD_NAME}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "-9999px",
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
        }}
      />

      {/* Datalist shared by the booking-system combobox below */}
      <datalist id={BOOKING_SYSTEM_DATALIST_ID}>
        {BOOKING_SYSTEM_SUGGESTIONS.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>

      {/* Venue */}
      <Section title="Venue">
        <FormField
          label="Venue name"
          htmlFor="venue_name"
          required
          error={errors.venue_name?.message}
        >
          <Input id="venue_name" {...register("venue_name")} />
        </FormField>

        <FormField
          label="Country"
          htmlFor="country"
          required
          error={errors.country?.message}
        >
          <select
            id="country"
            {...register("country")}
            className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {COUNTRIES.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </FormField>

        <FormField
          label="Address line 1"
          htmlFor="address_line1"
          required
          error={errors.address_line1?.message}
        >
          <Input id="address_line1" {...register("address_line1")} />
        </FormField>

        <FormField
          label="Address line 2"
          htmlFor="address_line2"
          error={errors.address_line2?.message}
        >
          <Input id="address_line2" {...register("address_line2")} />
        </FormField>

        <div className="grid gap-4 sm:grid-cols-3">
          <FormField
            label="City"
            htmlFor="city"
            required
            error={errors.city?.message}
          >
            <Input id="city" {...register("city")} />
          </FormField>
          <FormField
            label="State / region"
            htmlFor="region"
            required
            error={errors.region?.message}
          >
            <Input id="region" {...register("region")} />
          </FormField>
          <FormField
            label="Postcode / ZIP"
            htmlFor="postcode"
            required
            error={errors.postcode?.message}
          >
            <Input id="postcode" {...register("postcode")} />
          </FormField>
        </div>
      </Section>

      {/* Contact */}
      <Section title="Primary contact">
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label="Full name"
            htmlFor="contact_name"
            required
            error={errors.contact_name?.message}
          >
            <Input id="contact_name" {...register("contact_name")} />
          </FormField>
          <FormField
            label="Email"
            htmlFor="contact_email"
            required
            error={errors.contact_email?.message}
          >
            <Input
              id="contact_email"
              type="email"
              autoComplete="email"
              {...register("contact_email")}
            />
          </FormField>
        </div>
        <FormField
          label="Phone"
          htmlFor="contact_phone"
          required
          error={errors.contact_phone?.message}
        >
          <Input
            id="contact_phone"
            type="tel"
            autoComplete="tel"
            placeholder="e.g. +1 (212) 555-1234"
            {...register("contact_phone")}
          />
        </FormField>
      </Section>

      {/* Booking system */}
      <Section
        title="Booking system"
        description="If you already use software to manage court bookings, tell us which one. We'll integrate with it directly where possible. If not, you'll get a free Whistll venue dashboard after approval."
      >
        <FormField
          label="Do you use a booking system?"
          htmlFor="uses_booking_system_yes"
          required
          error={errors.uses_booking_system?.message}
        >
          <Controller
            control={control}
            name="uses_booking_system"
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-3">
                <RadioCard
                  id="uses_booking_system_yes"
                  name={field.name}
                  value="yes"
                  checked={field.value === "yes"}
                  onChange={field.onChange}
                  title="Yes"
                  body="I use booking software"
                />
                <RadioCard
                  id="uses_booking_system_no"
                  name={field.name}
                  value="no"
                  checked={field.value === "no"}
                  onChange={field.onChange}
                  title="No"
                  body="Use the Whistll dashboard"
                />
              </div>
            )}
          />
        </FormField>

        {usesBookingSystem === "yes" && (
          <FormField
            label="Which system?"
            htmlFor="booking_system"
            required
            hint="Start typing to see suggestions, or enter anything."
            error={errors.booking_system?.message}
          >
            <Input
              id="booking_system"
              list={BOOKING_SYSTEM_DATALIST_ID}
              autoComplete="off"
              placeholder="e.g. Bookteq, Skedda, CourtReserve…"
              {...register("booking_system")}
            />
          </FormField>
        )}
      </Section>

      {/* Notes */}
      <Section title="Anything else?">
        <FormField
          label="Notes for the Whistll team"
          htmlFor="notes"
          hint="Opening hours, peak times, number of courts, restrictions, anything useful."
          error={errors.notes?.message}
        >
          <Textarea id="notes" rows={5} {...register("notes")} />
        </FormField>
      </Section>

      {/* Terms */}
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-secondary/40 p-5">
        <label className="flex items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input accent-volt"
            {...register("accept_terms")}
          />
          <span>
            I'm authorised to represent this venue and agree to Whistll's{" "}
            <a
              href="/terms"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Terms
            </a>{" "}
            and{" "}
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noreferrer"
              className="underline underline-offset-2 hover:text-foreground"
            >
              Privacy Policy
            </a>
            . The Venue Partner Agreement (which covers fees,
            payouts, and venue-specific obligations) is the final
            step inside your dashboard once you're approved.
          </span>
        </label>
        {errors.accept_terms && (
          <p className="text-xs text-destructive">{errors.accept_terms.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          We'll respond within 48 hours.
        </p>
        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Submitting…
            </>
          ) : (
            "Submit application"
          )}
        </Button>
      </div>
    </form>
  );
}

function Section({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <div>
        <h3 className="text-lg font-semibold tracking-tight">{title}</h3>
        {description && (
          <p className="mt-1 text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

interface RadioCardProps {
  id: string;
  name: string;
  value: string;
  checked: boolean;
  onChange: (value: string) => void;
  title: string;
  body: string;
}

function RadioCard({
  id,
  name,
  value,
  checked,
  onChange,
  title,
  body,
}: RadioCardProps) {
  return (
    <label
      htmlFor={id}
      className={
        "flex cursor-pointer flex-col gap-1 rounded-lg border p-4 transition-all " +
        (checked
          ? "border-volt bg-volt-subtle ring-1 ring-volt"
          : "border-border bg-card hover:border-volt-border")
      }
    >
      <input
        id={id}
        type="radio"
        name={name}
        value={value}
        checked={checked}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      <span className="text-sm font-semibold">{title}</span>
      <span className="text-xs text-muted-foreground">{body}</span>
    </label>
  );
}

function SubmittedConfirmation({ id }: { id: string }) {
  // "blocked" is the sentinel we set for honeypot / timing-filter rejections.
  // We still show the success screen so bots don't learn they were caught,
  // but we skip the reference-ID strip because the id isn't real.
  const isBlocked = id === "blocked";

  return (
    <div className="flex flex-col items-center gap-6 rounded-2xl border border-border bg-card p-10 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-volt text-primary-foreground">
        <CheckCircle2 className="h-7 w-7" />
      </div>
      <div className="max-w-md space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">
          Application received
        </h2>
        <p className="text-sm text-muted-foreground">
          Thanks for applying to partner with Whistll. We'll review your
          submission and get back to you within 48 hours.
          {!isBlocked && (
            <>
              {" "}
              Your reference ID is{" "}
              <span className="rounded-md bg-secondary px-2 py-1 font-mono text-xs">
                {id}
              </span>
              .
            </>
          )}
        </p>
      </div>
    </div>
  );
}
